import React, { useState, useCallback, useRef } from 'react';
// --- external imports ------

import firebase from '~/authentication/lib/firebase';
import { validateEmail, errorText } from '~/authentication/lib/credentialValidation';
import { ERROR_CODES, handleBaseError } from '~/authentication/lib/errors';
import { EMAIL_MAX_LENGTH } from '~/authentication/lib/constants-auth';
import useAuthRequest from '~/authentication/lib/useAuthRequest';
import { LoadingCursor } from '~/containers/GameAsset/cursors';
// --- internal imports ------

import AuthBackdrop from '../AuthBackdrop';
import {
	ModalBackground,
	TitleText,
	InputBackground,
	ErrorMessage,
	FORM_WIDTH_PROJECTION,
} from '~/authentication/AuthAssets';
import {
	DescriptionText,
	FieldLabel,
	FieldInput,
	BackToLoginLink,
	ResetButton,
	INPUT_PROJECTION,
	ResetButtonText,
} from './ForgotPasswordPageAssets';
// --- sibling imports ------

const EMAIL_ACTION_CODE = {
	url: process.env.REACT_APP_FIREBASE_EMAIL_CONTINUE_URL || '',
	continueUrl: process.env.REACT_APP_FIREBASE_EMAIL_CONTINUE_URL || '',
};

function ForgotPasswordPage() {
	const [generalStatus, setGeneralStatus] = useState(null);
	const [emailError, setEmailError] = useState(null);

	const emailRef = useRef(null);
	const { isProcessing, runRequest } = useAuthRequest();

	const handleFirebaseError = useCallback((error) => {
		const code = error?.code;

		switch (code) {
			case ERROR_CODES.FIREBASE.FORGOT.INVALID_EMAIL:
				setEmailError('Please enter a valid email!');
				break;
			case ERROR_CODES.FIREBASE.FORGOT.USER_NOT_FOUND:
				setEmailError('The user associated with this email was not found!');
				break;
			default:
				setEmailError(handleBaseError(error));
		}
	}, []);

	const handleOnSubmit = useCallback(async () => {
		const email = emailRef.current?.value ?? '';

		const { isValid: isValidEmail, error: validEmailError } = validateEmail(email);
		setEmailError(validEmailError);
		if (!isValidEmail) {
			return;
		}

		try {
			await runRequest(() => firebase.sendPasswordResetEmail(email, EMAIL_ACTION_CODE));
			setGeneralStatus('Email correctly sent. Please check your inbox.');
		}
		catch (error) {
			handleFirebaseError(error);
		}
	}, [handleFirebaseError, runRequest]);

	const handleOnEmailBlur = useCallback((e) => {
		const { error } = validateEmail(e.target.value);
		setEmailError(error);
	}, []);

	return (
		<AuthBackdrop>
			{isProcessing && <LoadingCursor />}

			<ModalBackground
				x={528}
				y={148}
				shouldFadeIn={false}
			/>

			<TitleText
				x={644}
				y={212}
				projection={FORM_WIDTH_PROJECTION}
			>
				FORGOT{'\n'}PASSWORD
			</TitleText>

			{!generalStatus && (
				<DescriptionText
					x={644}
					y={468}
					projection={FORM_WIDTH_PROJECTION}
				>
					A link will be sent to your registration E-mail to help reset your password.
				</DescriptionText>
			)}

			{generalStatus && (
				<DescriptionText
					x={644}
					y={468}
					projection={FORM_WIDTH_PROJECTION}
				>
					{generalStatus}
				</DescriptionText>
			)}

			{!generalStatus && (
				<>
					{/* E-Mail field */}
					<FieldLabel
						x={644}
						y={580}
						projection={FORM_WIDTH_PROJECTION}
					>
						E-Mail
					</FieldLabel>

					<InputBackground
						x={644}
						y={636}
						projection={INPUT_PROJECTION}
					/>

					<FieldInput
						ref={emailRef}
						x={644}
						y={636}
						projection={INPUT_PROJECTION}
						type="email"
						fontSize={24}
						maxLength={EMAIL_MAX_LENGTH}
						onBlur={handleOnEmailBlur}
						disabled={isProcessing}
						hasError={!!emailError}
						data-testid="forgot-email-input"
					/>

					{emailError && (
						<ErrorMessage
							x={644}
							y={720}
							projection={FORM_WIDTH_PROJECTION}
						>
							{errorText(emailError)}
						</ErrorMessage>
					)}
				</>
			)}

			<BackToLoginLink
				x={644}
				y={760}
				projection={FORM_WIDTH_PROJECTION}
				to="/login"
			>
				Back to Login!
			</BackToLoginLink>

			{!generalStatus && (
				<>
					<ResetButton
						x={865}
						y={896}
						onClick={handleOnSubmit}
						disabled={isProcessing}
						shouldFadeIn={false}
						data-testid="forgot-submit"
					/>
					<ResetButtonText
						x={901}
						y={907}
						shouldFadeIn={false}
					>
						Send Link
					</ResetButtonText>
				</>
			)}

		</AuthBackdrop>
	);
}

export default ForgotPasswordPage;
